<template>
    <div>
        <div :class="{ 'cart-as-sidebar': asSidebar, visible: sidebarVisible }">
            <h1 v-if="!asSidebar" class="mb-2 mb-md-5 mb-xl-6">
                {{ $t("checkout.cart_title") }}
            </h1>

            <div v-if="isLoading" class="text-center">
                <span
                    class="spinner-border spinner-border"
                    role="status"
                    aria-hidden="true"
                ></span>
            </div>

            <div v-if="!isLoading && cart?.lineItems?.length < 1">
                <p>{{ $t("checkout.cart_label_empty") }}</p>
                <a v-if="productsUrl" :href="productsUrl" class="btn btn-secondary btn-sm">{{ $t('product_detail.all_products') }}</a>

                <div class="card coupon-card mt-6">
                    <div class="card-body">
                        <div class="meta">
                            <p class="title">
                                {{ $t("checkout.prepaid_box_title") }}
                            </p>
                            <p class="text">
                                {{ $t("checkout.prepaid_box_text") }}
                            </p>
                        </div>
                        <div class="action">
                            <a
                                href=""
                                class="btn btn-primary"
                                data-bs-toggle="modal"
                                data-bs-target="#modalCodeInput"
                            >
                                {{ $t("checkout.btn_input_prepaid_box_code") }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-show="!isLoading && cart?.lineItems?.length >= 1"
                class="row justify-content-between g-4 g-xl-2"
            >
                <div class="col-12 col-xl-7 col-cart-summary">
                    <h3 v-if="asSidebar" class="mb-2 mb-md-2 mb-xl-3">
                        {{ $t("checkout.cart_title") }}
                    </h3>

                    <CartLineItems :cart="cart" :is-lifestyle="isLifestyle" @update-cart="updateCart" />

                    <div class="card coupon-card mt-3">
                        <div class="card-body">
                            <div class="meta">
                                <p class="title">
                                    {{ $t("checkout.prepaid_box_title") }}
                                </p>
                                <p class="text">
                                    {{ $t("checkout.prepaid_box_text") }}
                                </p>
                            </div>
                            <div class="action">
                                <a
                                    href=""
                                    class="btn btn-primary"
                                    data-bs-toggle="modal"
                                    data-bs-target="#modalCodeInput"
                                >
                                    {{ $t("checkout.btn_input_prepaid_box_code") }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12 col-xl-4 col-cart-checkout">
                    <div class="card checkout-card checkout-card-delivery">
                        <div class="card-body">
                            <h5 class="mb-3">
                                {{ $t("checkout.delivery_title") }}
                            </h5>

                            <shipment-selector :cart="cart"></shipment-selector>
                        </div>
                    </div>

                    <cart-summary :cart="cart"></cart-summary>

                    <a
                        :href="checkoutUrl"
                        class="btn btn-secondary w-100 mt-1"
                        @click.prevent="handleCheckoutButtonClick"
                        :class="{'disabled': isProcessingCheckout}"
                    >
                        <span v-if="!isProcessingCheckout">
                            <span v-if="asSidebar">
                                {{ $t("checkout.btn_to_the_register") }}
                            </span>
                            <span v-else>
                                {{ $t("checkout.btn_continue_to_order") }}
                            </span>
                        </span>
                        <span v-else class="d-flex align-items-center justify-content-center">
                            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {{ $t("checkout.processing") }}
                        </span>
                    </a>

                    <div class="payment-method-info mt-1" v-if="paymentMethods && paymentMethods.length > 0 && !asSidebar">
                        <p>{{ $t("checkout.payment_safe_payment_with" )}}</p>
                        <div class="thumbs">
                            <template v-for="method in paymentMethods">
                                <img
                                    v-if="method.media"
                                    :src="method.media.url"
                                    :alt="method.translated.name + ' - Thumb'"
                                />
                            </template>
                        </div>
                    </div>

                    <button
                        v-if="asSidebar"
                        class="btn btn-primary w-100 mt-1"
                        @click="hideSidebar()"
                    >
                        <span>{{ $t("checkout.btn_continue_shopping") }}</span>
                    </button>
                </div>
            </div>
        </div>

        <div class="cart-as-sidebar-backdrop" :class="{ 'visible': sidebarVisible }"></div>
    </div>
</template>

<script>
import CartLineItems from "./CartLineItems.vue";
import ShipmentSelector from "./ShipmentSelector.vue";
import CartSummary from "./CartSummary.vue";
import shopware from '../../mixins/shopware';
import { EventBus } from "../../api/eventBus.ts";

export default {
    components: {
        CartLineItems,
        ShipmentSelector,
        CartSummary
    },
    mixins: [shopware],
    props: {
        asSidebar: Boolean,
        sidebarContinueUrl: String,
        productsUrl: String,
        checkoutUrlLogin: String,
        checkoutUrlCollectionPoint: String,
        isLifestyle: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            cart: null,
            cartFetchGen: 0,
            isLoading: false,
            isProcessingCheckout: false,
            isLoggedIn: false,
            sidebarVisible: false,
            paymentMethods: [],
            retryCount: 0,
            maxRetries: 3
        };
    },
    created: function () {
        this.init();
        this.documentClickHandler = this.handleDocumentClick.bind(this);
        // Listen for external cart restore requests
        EventBus.$on("restore-cart", this.restoreCartFromOrder);

        // Handle payment session expiration
        EventBus.$on("payment-session-expired", async () => {
            await this.getCart();
            EventBus.$emit('trigger-alert', {
                message: this.$t('checkout.payment_session_expired'),
                state: 'warning'
            });
        });

        // Listen for cart updates (e.g., from voucher input) - works in both sidebar and page mode
        EventBus.$on("cart-updated", (cart) => {
            if (cart && cart.lineItems) {
                // Mutation event arrived — invalidate any in-flight getCart so a late stale resolution cannot clobber this fresh state.
                this.cartFetchGen++;
                this.cart = cart;
            }
        });
    },
    beforeDestroy() {
        EventBus.$off("restore-cart");
        EventBus.$off("payment-session-expired");
        EventBus.$off("cart-updated");
        document.removeEventListener("click", this.documentClickHandler);
    },
    computed: {
        checkoutUrl() {
            if (!this.asSidebar) {
                if (this.isLoggedIn) {
                    return this.checkoutUrlCollectionPoint;
                } else {
                    return this.checkoutUrlLogin;
                }
            } else {
                return this.sidebarContinueUrl;
            }
        },
    },
    methods: {
        async init() {
            this.isLoading = true;

            try {
                await this.getContext();

                const promises = [
                    this.getCart(),
                    this.getProfile(),
                    this.getPaymentMethods(),
                ];

                await Promise.all(promises);

                if (this.asSidebar ?? null) {
                    this.sidebarListener();
                } else {
                    this.triggerViewCartEvent();
                }
            } catch (error) {
                console.error("Error initializing cart:", error);
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.cart_load_error'),
                    state: 'error'
                });
            } finally {
                this.isLoading = false;
            }
        },
        updateCart(updatedCart) {
            // Cart mutation arrived from a child (e.g., CartLineItems quantity/remove) — invalidate any in-flight getCart so its stale resolution cannot clobber this fresh state.
            this.cartFetchGen++;
            this.cart = updatedCart;
            // Update transaction ID in localStorage for consistent tracking
            if (updatedCart && updatedCart.token) {
                localStorage.setItem("transactionId", updatedCart.token);
            }
        },

        handleDocumentClick(event) {
            var flyoutBar = document.querySelector(".cart-as-sidebar");
            if (flyoutBar && !flyoutBar.contains(event.target)) {
                if (this.sidebarVisible) {
                    this.hideSidebar();
                }
            }
        },

        sidebarListener() {
            EventBus.$on("cart-updated", (cart) => {
                if (cart.errors && cart.errors.hasOwnProperty('faulty-voucher-product-combination')) {
                    EventBus.$emit('trigger-alert', {
                        message: this.getErrorMessage(cart.errors['faulty-voucher-product-combination']),
                        state: 'error'
                    });

                    this.hideSidebar();
                    return;
                }

                if (cart.lineItems && cart.lineItems.length > 0) {
                    Promise.all([
                        this.getCart(),
                        this.getProfile(),
                    ]).then(() => {
                        this.triggerViewCartEvent();
                    }).catch((error) => {
                        console.error("Error while fetching data:", error);
                        EventBus.$emit('trigger-alert', {
                            message: this.$t('checkout.cart_update_error'),
                            state: 'error'
                        });
                    });

                    if (!this.sidebarVisible) {
                        setTimeout(() => {
                            this.showSidebar();
                        }, 600);
                    }
                } else {
                    this.hideSidebar();
                }
            });
            document.addEventListener("click", this.documentClickHandler);
        },
        showSidebar() {
            this.sidebarVisible = true;
        },
        hideSidebar() {
            this.sidebarVisible = false;
        },

        async getProfile() {
            try {
                await this.api.client.invoke("readCustomer post /account/customer", {});
                this.isLoggedIn = true;
            } catch (error) {
                this.isLoggedIn = false;
            }
        },

        async getCart() {
            const fetchGen = ++this.cartFetchGen;
            try {
                const cart = await this.api.client.invoke("readCart get /checkout/cart");

                // Only assign if no newer cart arrived via cart-updated while we were waiting.
                if (fetchGen === this.cartFetchGen) {
                    this.cart = cart;
                    localStorage.setItem("transactionId", cart.token);
                }

                // Check if we need to restore from a failed order
                const failedOrderId = localStorage.getItem("lastOrderId");
                if (failedOrderId) {
                    await this.restoreCartFromOrder(failedOrderId);
                }

                return this.cart;
            } catch (error) {
                console.error("Error fetching cart:", error);
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    console.log(`Retrying cart fetch (${this.retryCount}/${this.maxRetries})...`);
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retry
                    return await this.getCart();
                } else {
                    EventBus.$emit('trigger-alert', {
                        message: this.$t('checkout.cart_load_error'),
                        state: 'error'
                    });
                    this.retryCount = 0;
                    throw error;
                }
            }
        },

        async restoreCartFromOrder(orderId) {
            const orderIdToRestore = orderId || localStorage.getItem("lastOrderId");
            if (!orderIdToRestore) return;

            try {
                this.isLoading = true;

                const response = await this.api.client.invoke("readOrder post /order", {
                    "limit": 1,
                    "ids": [orderIdToRestore],
                    "associations": {
                        "lineItems": {},
                        "deliveries": {
                            "associations": {
                                "shippingMethod": {}
                            }
                        }
                    }
                });

                if (!response.orders || !response.orders.elements || response.orders.elements.length === 0) {
                    console.error("Failed to fetch order to restore");
                    localStorage.removeItem("lastOrderId");
                    return;
                }

                const failedOrder = response.orders.elements[0];

                // Store transaction ID for the new cart
                localStorage.setItem("transactionId", this.cart.token);

                // Add items from the failed order to the new cart
                const lineItemPromises = failedOrder.lineItems
                    .filter(lineItem => lineItem.payload.acrisProductVoucherCodes)
                    .map(lineItem =>
                    this.api.client.invoke("addLineItem post /checkout/cart/line-item", {
                        items: [{
                            id: lineItem.id,
                            modified: lineItem.modified,
                            type: lineItem.type,
                            referencedId: lineItem.referencedId,
                            quantity: lineItem.quantity,
                            good: lineItem.good,
                            payload: {
                                productUrl: lineItem.payload?.productUrl,
                                description: lineItem.payload?.description,
                                variant: lineItem.payload?.variant,
                                coverImg: lineItem.payload?.coverImg
                            }
                        }]
                    })
                );

                await Promise.all(lineItemPromises);

                // Restore shipping method if available
                if (failedOrder.deliveries &&
                    failedOrder.deliveries.length > 0 &&
                    failedOrder.deliveries[0].shippingMethod) {
                    await this.api.client.invoke("updateContext patch /context", {
                        shippingMethodId: failedOrder.deliveries[0].shippingMethod.id
                    });
                }

                // Restore dealer if available
                if (failedOrder.customFields && failedOrder.customFields.dealer) {
                    await this.api.client.invoke(`dealerToCart patch /checkout/cart/dealer`, {
                        token: this.context.token,
                        customer: this.context.customer.id,
                        dealer: failedOrder.customFields.dealer
                    });
                }

                // Get the updated cart with proper generation gating: capture our own fetchGen BEFORE the await
                // so a cart-updated event arriving mid-await cannot be clobbered by our late assignment.
                // If superseded, we abort the entire restore broadcast (no toast, no emit) and defer to the newer cart.
                const fetchGen = ++this.cartFetchGen;
                const restoredCart = await this.api.client.invoke("readCart get /checkout/cart");
                if (fetchGen !== this.cartFetchGen) {
                    // A newer cart-updated event arrived during the await. That cart is now authoritative; do not clobber.
                    return;
                }
                this.cart = restoredCart;

                // Notify user
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.cart_restored'),
                    state: 'success'
                });

                localStorage.removeItem("lastOrderId");
                EventBus.$emit("cart-updated", restoredCart);
            } catch (error) {
                console.error("Error restoring cart from order:", error);
                if (error.status && (error.status === 403 || error.status === 404)) {
                    return;
                }
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.cart_restore_error'),
                    state: 'error'
                });
            } finally {
                this.isLoading = false;
            }
        },

        async handleCheckoutButtonClick() {
            try {
                this.isProcessingCheckout = true;

                // First trigger GTM event
                this.triggerStartCheckoutEvent();

                // Verify cart is valid before proceeding
                const cartResponse = await this.api.client.invoke("readCart get /checkout/cart");
                if (!cartResponse || !cartResponse.lineItems || cartResponse.lineItems.length === 0) {
                    EventBus.$emit('trigger-alert', {
                        message: this.$t('checkout.empty_cart'),
                        state: 'warning'
                    });
                    return;
                }

                // Save cart token before redirect
                localStorage.setItem("transactionId", cartResponse.token);

                // Redirect to the checkout URL
                window.location.href = this.checkoutUrl;
            } catch (error) {
                console.error("Error during checkout button click handling:", error);
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.checkout_process_error'),
                    state: 'error'
                });
            } finally {
                this.isProcessingCheckout = false;
            }
        },

        triggerViewCartEvent() {
            if (this.cart && this.cart.lineItems && this.cart.lineItems.length > 0) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    'event': 'view_cart',
                    'items': this.getGTMItems()
                });
            }
        },

        triggerStartCheckoutEvent() {
            if (this.cart && this.cart.lineItems && this.cart.lineItems.length > 0) {
                let items = this.getGTMItems();

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    'event': 'begin_checkout',
                    'value': this.cart.price.totalPrice,
                    'number_of_items': items.length,
                    'items': items
                });
            }
        },

        getGTMItems() {
            if (!this.cart || !this.cart.lineItems) return [];

            return this.cart.lineItems.map((item, index) => ({
                item_id: item.payload?.productNumber || '',
                item_name: item.label,
                item_list_name: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher'),
                item_brand: item.payload?.manufacturer?.name || "DCM",
                price: item.price?.unitPrice || 0,
                quantity: item.quantity || 1,
                index: index + 1,
                currency: 'EUR',
                item_category: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher')
            }));
        },

        async getPaymentMethods() {
            try {
                const paymentMethods = await this.api.client.invoke("readPaymentMethod post /payment-method");
                this.paymentMethods = paymentMethods.elements.filter(item => item.mediaId !== null);
            } catch (error) {
                console.error("Error fetching payment methods:", error);
            }
        },
    }
}
</script>
